import React, { useRef, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import Link from 'next/link';
import Tooltip from 'components/Tooltip';
import { Button, Anchor, Image, Loader } from './styles';
import { useRouter } from 'next/router';
import { getEnv } from 'utils';
import capitalizeString from 'utils/capitalizeString';
import normaliseText from 'utils/normaliseText';

const DEFAULT_PROPS = {
  onClick: undefined,
  href: undefined,
  className: undefined,
  title: undefined,
  isLoading: false,
};

export const Props = {
  src: PropTypes.string.isRequired,
  alt: PropTypes.string.isRequired,
  href: PropTypes.string,
  onClick: PropTypes.func,
  className: PropTypes.string,
  title: PropTypes.string,
  isLoading: PropTypes.bool,
};

export default function IllustrationThumb({
  className,
  src,
  alt,
  href,
  onClick,
  title,
  isLoading,
}) {
  const imageRef = useRef();
  const image = (
    <Image
      ref={imageRef}
      src={src}
      width={117}
      height={88}
      alt={`${capitalizeString(normaliseText(alt))} Icons, Symbols, Pictures, and Images`}
      title={`${capitalizeString(normaliseText(alt))} - Edit and Customize Icons of ${capitalizeString(normaliseText(alt))}`}
      loading="lazy"
    />
  );
  const [hasError, setHasError] = useState(false);
  const router = useRouter();

  useEffect(() => {
    function handleImageError() {
      setHasError(true);
    }

    if (imageRef.current) {
      imageRef.current.addEventListener('error', handleImageError);
    }

    return () => {
      if (imageRef.current) {
        imageRef.current.removeEventListener('error', handleImageError);
      }
    };
  }, []);

  function withTooltip(children) {
    return (
      <Tooltip href={href} title={title}>
        {children}
      </Tooltip>
    );
  }

  if (hasError) {
    return null;
  }

  let output = image;

  if (onClick) {
    output = (
      <Button isLoading={isLoading} className={className} onClick={onClick} type="button">
        {image}
      </Button>
    );
  }

  if (href) {
    output = (
      <Link href={href} prefetch={false} className={className}>
        <Anchor
          href={`${getEnv('MTG_URL_APP')}${router.basePath}${href}`}
          isLoading={isLoading}
          className={className}
        >
          {image}
        </Anchor>
      </Link>
    );
  }

  if (title) {
    return withTooltip(output);
  }

  return output;
}

IllustrationThumb.propTypes = Props;
IllustrationThumb.Loader = Loader;
IllustrationThumb.defaultProps = DEFAULT_PROPS;
